/**
 * SEO Metadata Types
 *
 * Comprehensive type definitions for SEO metadata management
 * following Vue 3 and modern web standards best practices.
 */

/**
 * Open Graph metadata for social media sharing
 * @see https://ogp.me/
 */
export interface OpenGraphMetadata {
  /** The title of your object as it should appear within the graph */
  title?: string;
  /** The type of your object, e.g., "website", "article" */
  type?: 'website' | 'article' | 'product' | 'profile';
  /** An image URL which should represent your object within the graph */
  image?: string;
  /** The canonical URL of your object that will be used as its permanent ID in the graph */
  url?: string;
  /** A one to two sentence description of your object */
  description?: string;
  /** The locale these tags are marked up in. Of the format language_TERRITORY */
  locale?: string;
  /** The word that appears before this object's title in a sentence */
  siteName?: string;
  /** Additional image properties */
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
}

/**
 * Twitter Card metadata for Twitter sharing
 * @see https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/abouts-cards
 */
export interface TwitterCardMetadata {
  /** The card type, which will be one of "summary", "summary_large_image", "app", or "player" */
  card?: 'summary' | 'summary_large_image' | 'app' | 'player';
  /** @username of website. Either twitter:site or twitter:site:id is required */
  site?: string;
  /** @username of content creator */
  creator?: string;
  /** Title of content (max 70 characters) */
  title?: string;
  /** Description of content (max 200 characters) */
  description?: string;
  /** URL of image to use in the card */
  image?: string;
  /** A text description of the image conveying the essential nature of an image to users who are visually impaired */
  imageAlt?: string;
}

/**
 * Structured data for rich snippets
 * @see https://schema.org/
 */
export interface StructuredData {
  /** Schema.org type (e.g., "WebPage", "Article", "Product") */
  '@type': string;
  /** Additional schema.org properties */
  [key: string]: unknown;
}

/**
 * Complete SEO metadata configuration
 */
export interface SeoMetadata {
  /** Page title (will be suffixed with site name) */
  title: string;
  /** Meta description (150-160 characters recommended) */
  description: string;
  /** Meta keywords (comma-separated, optional - not used by Google but may help other search engines) */
  keywords?: string;
  /** Canonical URL (absolute URL to prevent duplicate content issues) */
  canonical?: string;
  /** Robots meta tag (e.g., "index,follow", "noindex,nofollow") */
  robots?: string;
  /** Language code (e.g., "de", "en") */
  lang?: string;
  /** Open Graph metadata for social sharing */
  openGraph?: OpenGraphMetadata;
  /** Twitter Card metadata */
  twitter?: TwitterCardMetadata;
  /** JSON-LD structured data */
  structuredData?: StructuredData | StructuredData[];
  /** Additional custom meta tags */
  customMeta?: Array<{
    name?: string;
    property?: string;
    content: string;
  }>;
}


// TODO: these arnt types move them to a proper location
/**
 * Default SEO configuration
 */
export const DEFAULT_SEO: Partial<SeoMetadata> = {
  title: 'Lerntypentest',
  description: 'Entdecken Sie den Lerntyp Ihres Kindes mit unserem wissenschaftlich fundierten 90-Fragen-Test. Kostenlos, ohne Anmeldung, sofortige Ergebnisse.',
  lang: 'de',
  robots: 'index,follow',
  openGraph: {
    type: 'website',
    locale: 'de_DE',
    siteName: 'Lerntypentest',
  },
  twitter: {
    card: 'summary_large_image',
  },
};

/**
 * Site-wide SEO configuration
 */
export const SITE_CONFIG = {
  siteName: 'Lerntypentest',
  siteUrl: typeof window !== 'undefined' ? window.location.origin : '',
  defaultImage: '/images/og-default.jpg',
  twitterHandle: '@lerntypentest',
  separator: '|',
} as const;
